'use client';

import React, { createContext, ElementType, PropsWithChildren, ReactNode, useContext, useMemo } from 'react';
import clsx from 'clsx';
import { Component } from '@headless-workspace/typings';
import { GlowDivider, GlowDividerProps, GlowIconProps, GlowImageClickable, ImageClickableProps } from '../atoms';
import {
    IconComponentType,
    PropsWithAriaLabel,
    PropsWithClassName,
    PropsWithDataLayer,
    PropsWithFontFamily,
    PropsWithTagName,
    PropsWithTestId,
} from '../props';
import { Icons } from '../res/icons';
import fullClickableWithInvisibleElement from '../utils/css/fullClickableWithInvisibleElement.module.css';
import { CardBackground, CardBorder, CardClassNames, CardDirection, CardSize, CardStyle, CardType } from './CardStyles';
import { CardButtonProps, ComposableContent, CTA } from './ComposableContent';

type TextProps = PropsWithClassName &
    PropsWithTagName &
    Partial<PropsWithFontFamily> & {
        text: ReactNode;
    };

export type ContentProps = {
    title?: TextProps;
    description?: TextProps;
    legalMention?: TextProps;
    button?: CardButtonProps;
};

export type CardContentProps = PropsWithClassName &
    ContentProps &
    PropsWithChildren &
    Partial<PropsWithFontFamily> & {
        subTitle?: ReactNode;
        label?: ReactNode;
        content?: Node;
        tags?: CTA[];
        hasImage?: boolean;
        hasRightIcon?: boolean;
        iconLeft?: boolean;
        Icon?: IconComponentType;
        classNames?: CardClassNames;
        image?: ImageClickableProps;
        iconType?: GlowIconProps['type'];
        isLoading?: boolean;
    };

export type SelectableCardProps = {
    isSelected: boolean;
    isDisabled?: boolean;
} & (
    | {
          SelectableTag?: 'a';
          href: string;
      }
    | {
          SelectableTag?: 'button';
          onSelectCard: () => void;
      }
);

// Types and Enums
export type CardProps = ContainerProps &
    PropsWithChildren &
    PropsWithDataLayer &
    PropsWithAriaLabel &
    PropsWithTestId & {
        image?: ImageClickableProps;
        classNames?: {
            container?: string;
            wrapper?: string;
        };
        content?: CardContentProps;
    };

export type AdvancedCardProps = CardProps & SelectableCardProps;

export type TileProps = CardProps & {
    onClick?: () => void;
    href?: string;
};

type CardContextProps = {
    size?: CardSize;
    direction?: CardDirection;
    type?: CardType;
};

type ContainerProps = PropsWithChildren &
    PropsWithTagName &
    PropsWithClassName &
    PropsWithDataLayer &
    PropsWithAriaLabel &
    Omit<CardContextProps, 'type'> & {
        background?: CardBackground;
        border?: CardBorder;
        classNames?: CardClassNames;
    };

export type CardContainerProps<T extends ElementType> = ContainerProps & React.ComponentPropsWithoutRef<T>;

export type AdvancedCardContainerProps<T extends ElementType> = CardContainerProps<T> & SelectableCardProps;

type CardImage = ImageClickableProps & PropsWithAriaLabel;

// Context
const CardContext = createContext<CardContextProps>({
    size: CardSize.M,
    direction: CardDirection.Col,
    type: CardType.Card,
});

const useCardContext = () => useContext(CardContext);

// Root Component
type CardRootProps = CardContextProps & PropsWithChildren;

const Root: Component<CardRootProps> = ({
    type = CardType.Card,
    size = CardSize.M,
    direction = CardDirection.Col,
    children,
}: CardRootProps) => {
    return (
        <CardContext.Provider value={useMemo(() => ({ size, direction, type }), [direction, size, type])}>
            {children}
        </CardContext.Provider>
    );
};

// Wrapper Component
type CardWrapperProps = PropsWithClassName & PropsWithChildren & PropsWithAriaLabel;

const Wrapper: Component<CardWrapperProps> = ({ className, children }: CardWrapperProps) => {
    return <div className={className}>{children}</div>;
};

const defaultClasses = {
    width: 'w-full',
    height: 'h-auto',
    align: 'items-center',
};

// Container Component
const Container = <T extends ElementType = 'div'>({
    TagName: Component = 'div',
    className,
    children,
    dataLayer,
    ariaLabel,
    border = CardBorder.Default,
    classNames,
    background,
    dataTestId,
    ...props
}: CardContainerProps<T>) => {
    const { size, direction } = useCardContext();
    const { width, height, align } = { ...defaultClasses, ...classNames };

    return (
        <Component
            className={clsx(
                CardStyle.base,
                CardStyle.direction({ direction }),
                CardStyle.gap({ size }),
                CardStyle.getBorderClass({ border }),
                width,
                height,
                align,
                className,
                dataLayer?.className,
            )}
            data-layer-banner-action={dataLayer?.action}
            data-layer-id={dataLayer?.id}
            data-layer-name={dataLayer?.name}
            aria-label={ariaLabel}
            data-testid={dataTestId}
            {...props}
        >
            {children}
        </Component>
    );
};

const SELECTABLE_ELEMENT_NAME = 'selectable element';

const SelectableContainer = <T extends ElementType = 'div'>({
    className,
    children,
    dataLayer,
    ariaLabel,
    border = CardBorder.Default,
    classNames,
    background,
    isSelected,
    isDisabled = false,
    onSelectCard,
    inputType,
    SelectableTag = 'button',
    ...props
}: AdvancedCardContainerProps<T>) => {
    const { size, direction } = useCardContext();
    const { width, height, align } = { ...defaultClasses, ...classNames };
    const selected = isDisabled ? false : isSelected;

    return (
        <div
            className={clsx(
                CardStyle.base,
                CardStyle.direction({ direction }),
                CardStyle.gap({ size }),
                CardStyle.getBorderClass({ border, selected }),
                width,
                height,
                align,
                fullClickableWithInvisibleElement.container,
                className,
            )}
            data-layer-banner-action={dataLayer?.action}
            data-layer-id={dataLayer?.id}
            data-layer-name={dataLayer?.name}
            aria-label={ariaLabel}
            {...props}
        >
            <SelectableTag
                href={props.href}
                className={fullClickableWithInvisibleElement.invisibleElement}
                onClick={isDisabled ? undefined : onSelectCard}
            >
                <div className={'invisible'}>{SELECTABLE_ELEMENT_NAME}</div>
            </SelectableTag>
            {children}
        </div>
    );
};

// Content Component
const Content: Component<CardContentProps> = ({
    children,
    className,
    hasImage,
    title,
    description,
    button,
    legalMention,
    hasRightIcon,
    classNames,
    Icon,
    iconType,
    isLoading,
}: CardContentProps) => {
    const { size = CardSize.M, direction = CardDirection.Row, type } = useCardContext();
    const { width, height, align } = { ...defaultClasses, ...classNames };

    return (
        <div
            className={clsx(
                'flex',
                CardStyle.padding({ size }),
                CardStyle.gap({ size }),
                hasImage && CardStyle.paddingWithImage[size][direction],
                width,
                height,
                align,
                className,
            )}
        >
            {!hasImage && Icon && <ComposableContent.Icon Icon={Icon} type={iconType} />}
            <div className={clsx('flex flex-col justify-between w-full', CardStyle.gap({ size }))}>
                <div className={'flex flex-col gap-0.5'}>
                    {title && (
                        <ComposableContent.Text
                            fontSize={'body'}
                            TagName={title.TagName}
                            fontFamily={title.fontFamily}
                            className={clsx('line-clamp-2', title.className)}
                            content={title.text}
                            fontWeight={'bold'}
                        />
                    )}
                    {description && (
                        <ComposableContent.Text
                            fontSize={'bodySmall'}
                            TagName={description.TagName}
                            fontFamily={description.fontFamily}
                            className={clsx('line-clamp-2', description.className)}
                            content={description.text}
                        />
                    )}
                    {children}
                </div>
                <ComposableContent.Button {...button} />
                <ComposableContent.LegalMention {...legalMention}>{legalMention?.text}</ComposableContent.LegalMention>
            </div>
            {hasRightIcon &&
                (type === CardType.Tile || type === CardType.CardAdvanced) &&
                (isLoading ? (
                    <ComposableContent.Icon
                        Icon={Icons.Spinner}
                        type={'medium'}
                        iconClassName={'animate-spin fill-text-primary-inverse'}
                    />
                ) : (
                    <ComposableContent.Icon Icon={Icons.ChevronRight} />
                ))}
        </div>
    );
};

// Image Component
const Image: Component<CardImage> = ({ classNames, TagName, ...props }: CardImage) =>
    props.src && (
        <GlowImageClickable
            classNames={{
                image: clsx('flex object-cover overflow-hidden w-full', classNames?.image),
                cta: clsx('h-full w-full', classNames?.cta),
                wrapper: clsx('w-full', classNames?.wrapper),
            }}
            TagName={TagName}
            {...props}
        />
    );

// Separator Component
const Separator: Component<GlowDividerProps> = ({ ...props }: GlowDividerProps) => <GlowDivider {...props} />;

// Exported ComposableCard
export const ComposableCard = {
    Root,
    Container,
    SelectableContainer,
    Wrapper,
    Image,
    Content,
    Separator,
};
